import { useEffect, useRef, useState } from "react";
import { ProvenanceBadge } from "@/components/Badges";

export const useAutosave = (initial, save, delay = 900) => {
  const [values, setValues] = useState(initial || {});
  const [state, setState] = useState("saved");
  const pending = useRef({});
  const timer = useRef(null);

  useEffect(() => {
    setValues(initial || {});
  }, [initial]);

  const flush = async () => {
    const body = pending.current;
    pending.current = {};
    if (!Object.keys(body).length) return;
    setState("saving");
    try {
      await save(body);
      setState("saved");
    } catch {
      setState("error");
    }
  };

  const set = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    pending.current[k] = v;
    setState("unsaved");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, delay);
  };

  useEffect(() => () => clearTimeout(timer.current), []);
  return { values, set, state, flush };
};

export const SaveState = ({ state }) => {
  const text = { saved: "All changes saved", saving: "Saving…", unsaved: "Unsaved changes", error: "Save failed - retrying on next edit" }[state];
  return <span data-testid="autosave-state" aria-live="polite" className={`caption ${state === "error" ? "text-red-600" : ""}`}>{text}</span>;
};

export const Field = ({ id, label, hint, prov, children }) => (
  <div>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <label htmlFor={id} className="label">{label}</label>
      {prov && <ProvenanceBadge source={prov.source} docName={prov.doc_name} />}
    </div>
    {children}
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export const TextField = ({ name, label, hint, values, set, prov, area, type = "text", placeholder }) => (
  <Field id={`f-${name}`} label={label} hint={hint} prov={prov?.[name]}>
    {area ? (
      <textarea id={`f-${name}`} data-testid={`field-${name}`} rows={4} className="input" value={values[name] || ""} placeholder={placeholder} onChange={(e) => set(name, e.target.value)} />
    ) : (
      <input id={`f-${name}`} data-testid={`field-${name}`} type={type} className="input" value={values[name] || ""} placeholder={placeholder} onChange={(e) => set(name, e.target.value)} />
    )}
  </Field>
);

export const SelectField = ({ name, label, hint, values, set, prov, options }) => {
  const opts = Array.isArray(options) ? options.map((o) => [o, o]) : Object.entries(options);
  return (
    <Field id={`f-${name}`} label={label} hint={hint} prov={prov?.[name]}>
      <select id={`f-${name}`} data-testid={`field-${name}`} className="input" value={values[name] || ""} onChange={(e) => set(name, e.target.value)}>
        <option value="">Select…</option>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
};
